import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { buildRowForInstrument } from '../../lib/surveySubmissionResults';

const SURVEY_META = [
  {
    id: 'psqi',
    shortTitle: 'PSQI',
    title: '피츠버그 수면의 질 질문지(PSQI)',
    info: '다음은 지난 한 달(4주) 동안 당신의 일상적인 수면 습관에 관한 질문입니다. 지난 한 달 동안 대부분의 일상에서 가장 적합한 답변에 표시 혹은 기록을 해주시길 바랍니다.',
  },
  {
    id: 'isi',
    shortTitle: 'ISI',
    title: '불면증 심각도 척도(ISI)',
    info: '아래 각 질문에서 수면 상황을 고려하여 해당 번호에 표시해주십시오. 현재(지난 2주) 불면문제(들)의 심각도를 평가하십시오.',
  },
  {
    id: 'berlin',
    shortTitle: 'Berlin',
    title: 'Berlin Questionnaire',
    info: '총 10문항 / 3개 영역(category)으로 구성된 자기보고식 질문지 / 수면무호흡 감지',
  },
  {
    id: 'oabss',
    shortTitle: 'OABSS',
    title: '과민성방광 증상 질문지(OABSS)',
    info: '아래 각 질문에서 수면 상황을 고려하여 해당 번호에 표시해주십시오. 현재(지난 2주) 불면문제(들)의 심각도를 평가하십시오.',
  },
];

const SURVEY_META_BY_ID = Object.fromEntries(SURVEY_META.map((s) => [s.id, s]));

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

function asObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

function getLikertLabels(q) {
  const opts = q.options;
  if (Array.isArray(opts)) {
    return opts.filter((v) => typeof v === 'string');
  }
  const obj = asObject(opts);
  if (Array.isArray(obj.labels)) {
    return obj.labels.filter((v) => typeof v === 'string');
  }
  return [];
}

function getLikertPointLabels(q, labels) {
  const opts = q.options;
  if (Array.isArray(opts)) {
    const metaEntry = opts.find((v) => v && typeof v === 'object' && !Array.isArray(v));
    const meta = asObject(metaEntry?.meta);
    if (Array.isArray(meta.point_labels)) return meta.point_labels;
    return labels.map((_, idx) => `${idx}점`);
  }
  const meta = asObject(asObject(opts).meta);
  if (Array.isArray(meta.point_labels)) return meta.point_labels;
  return labels.map((_, idx) => `${idx}점`);
}

function isVisibleQuestion(q, answerMap) {
  const showWhen = asObject(q.show_when);
  const field = showWhen.field;
  if (!field) return true;
  const expected = showWhen.equals;
  return String(answerMap[field] ?? '') === String(expected ?? '');
}

function getSectionHeading(instrumentCode, sectionLabel) {
  if (instrumentCode === 'psqi' && String(sectionLabel) === '5') {
    return '5. 지난 한 달 동안, 당신은 아래의 이유로 잠자는 데 얼마나 자주 문제가 있었습니까?';
  }
  return '';
}

function getNumberPairMeta(q) {
  if (q.input_type !== 'number') return null;
  const options = asObject(q.options);
  if (options.mode !== 'hour_minute_pair') return null;
  const hour = asObject(options.hour);
  const minute = asObject(options.minute);
  if (!hour.key || !minute.key) return null;
  return { hour, minute };
}

function isQuestionEmpty(q, answerMap) {
  const pair = getNumberPairMeta(q);
  if (pair) {
    return isEmpty(answerMap[pair.hour.key]) || isEmpty(answerMap[pair.minute.key]);
  }
  return isEmpty(answerMap[q.question_key]);
}

function countRemainingByQuestions(questions, answerMap) {
  if (!Array.isArray(questions) || !questions.length) return 0;
  return questions.reduce((acc, q) => {
    if (!isVisibleQuestion(q, answerMap)) return acc;
    return acc + (isQuestionEmpty(q, answerMap) ? 1 : 0);
  }, 0);
}

function Likert({ name, value, onChange, labels, pointLabels }) {
  return (
    <div className={`db-survey-likert-row ${labels.length >= 5 ? 'db-survey-likert-row--5' : ''}`}>
      {labels.map((label, idx) => (
        <label
          key={`${name}-${idx}`}
          className={`db-survey-chip ${labels.length >= 5 ? 'db-survey-chip--sm' : ''} ${
            value === String(idx) ? 'db-survey-chip--on' : ''
          }`}
        >
          <input
            type="radio"
            name={name}
            value={idx}
            checked={value === String(idx)}
            onChange={() => onChange(String(idx))}
          />
          <span className="db-survey-chip-label">
            {pointLabels?.[idx] ? (
              <span className="db-survey-chip-pts">{pointLabels[idx]}</span>
            ) : null}
            {label}
          </span>
        </label>
      ))}
    </div>
  );
}

function BerlinRadio({ name, value, onChange, options, disabled }) {
  return (
    <div className="db-survey-radio-list">
      {options.map((o) => (
        <label
          key={`${name}-${o.v}`}
          className={`db-survey-radio-line ${disabled ? 'db-survey-radio-line--disabled' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={o.v}
            checked={value === o.v}
            disabled={disabled}
            onChange={() => onChange(o.v)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export function DashboardSurveyModal({ open, onClose, userId, cohortCareOptionId, phase = 'pre' }) {
  const [activeId, setActiveId] = useState('psqi');
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [surveyMeta, setSurveyMeta] = useState(SURVEY_META);
  const [questionsByCode, setQuestionsByCode] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const surveyBodyRef = useRef(null);

  const currentQuestions = questionsByCode[activeId] || [];
  const currentAnswerMap = answers[activeId] || {};
  const activeSurveyCodes = useMemo(
    () => surveyMeta.map((s) => s.id).filter((code) => (questionsByCode[code] || []).length > 0),
    [surveyMeta, questionsByCode]
  );

  const setAnswer = useCallback((instrumentCode, key, value) => {
    setAnswers((prev) => ({
      ...prev,
      [instrumentCode]: {
        ...(prev[instrumentCode] || {}),
        [key]: value,
      },
    }));
  }, []);

  const remainingByCode = useMemo(() => {
    const out = {};
    activeSurveyCodes.forEach((code) => {
      out[code] = countRemainingByQuestions(questionsByCode[code] || [], answers[code] || {});
    });
    return out;
  }, [activeSurveyCodes, questionsByCode, answers]);
  const remaining = remainingByCode[activeId] || 0;
  const totalRemaining = useMemo(
    () => Object.values(remainingByCode).reduce((sum, n) => sum + n, 0),
    [remainingByCode]
  );
  const allInstrumentsComplete = activeSurveyCodes.length > 0 && totalRemaining === 0;

  useEffect(() => {
    if (!open || !cohortCareOptionId) return;
    let cancelled = false;

    (async () => {
      setIsLoadingMeta(true);
      const { data, error } = await supabase
        .from('cohort_survey_instruments')
        .select(
          'display_order, instrument:survey_instruments!inner(id, code, short_title, title, description, is_active)'
        )
        .eq('cohort_care_option_id', cohortCareOptionId)
        .order('display_order', { ascending: true });

      if (cancelled) return;
      setIsLoadingMeta(false);

      if (error) {
        console.error('[dashboardSurveyModal cohort_survey_instruments]', error);
        setSurveyMeta(SURVEY_META);
        return;
      }

      const mapped = (data || [])
        .map((row) => row.instrument)
        .filter((i) => i?.code && i?.is_active !== false)
        .map((i) => ({
          id: i.code,
          instrumentId: i.id,
          shortTitle:
            i.short_title || SURVEY_META_BY_ID[i.code]?.shortTitle || i.code.toUpperCase(),
          title: i.title || SURVEY_META_BY_ID[i.code]?.title || i.code.toUpperCase(),
          info: i.description || SURVEY_META_BY_ID[i.code]?.info || '',
        }));

      setSurveyMeta(mapped.length ? mapped : SURVEY_META);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, cohortCareOptionId]);

  useEffect(() => {
    if (!open || !surveyMeta.length) return;
    let cancelled = false;

    (async () => {
      setIsLoadingQuestions(true);
      const codeList = surveyMeta.map((s) => s.id);
      const { data: instrumentRows, error: instErr } = await supabase
        .from('survey_instruments')
        .select('id, code')
        .in('code', codeList);

      if (cancelled) return;
      if (instErr || !instrumentRows?.length) {
        setIsLoadingQuestions(false);
        console.error('[dashboardSurveyModal survey_instruments]', instErr);
        setQuestionsByCode({});
        return;
      }

      const instrumentIdToCode = new Map((instrumentRows || []).map((r) => [r.id, r.code]));
      const { data: qRows, error: qErr } = await supabase
        .from('survey_questions')
        .select(
          'instrument_id, question_key, section_label, prompt, input_type, options, show_when, display_order'
        )
        .in(
          'instrument_id',
          (instrumentRows || []).map((r) => r.id)
        )
        .order('display_order', { ascending: true });

      if (cancelled) return;
      setIsLoadingQuestions(false);

      if (qErr) {
        console.error('[dashboardSurveyModal survey_questions]', qErr);
        setQuestionsByCode({});
        return;
      }

      const grouped = {};
      (qRows || []).forEach((q) => {
        const code = instrumentIdToCode.get(q.instrument_id);
        if (!code) return;
        if (!grouped[code]) grouped[code] = [];
        grouped[code].push(q);
      });
      setQuestionsByCode(grouped);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, surveyMeta]);

  useEffect(() => {
    if (!open || !userId || !cohortCareOptionId) return;
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from('survey_submissions')
        .select('answers, phase, instrument:survey_instruments!inner(code)')
        .eq('user_id', userId)
        .eq('cohort_care_option_id', cohortCareOptionId)
        .eq('phase', phase);

      if (cancelled) return;
      if (error) {
        console.error('[dashboardSurveyModal survey_submissions]', error);
        return;
      }

      const nextAnswers = {};
      const nextSubmitted = {};
      (data || []).forEach((row) => {
        const code = row?.instrument?.code;
        if (!code) return;
        nextAnswers[code] = { ...(row.answers || {}) };
        nextSubmitted[code] = true;
      });

      setAnswers(nextAnswers);
      setSubmitted(nextSubmitted);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, userId, cohortCareOptionId, phase]);

  useEffect(() => {
    if (!surveyMeta.some((s) => s.id === activeId)) {
      setActiveId(surveyMeta[0]?.id || 'psqi');
    }
  }, [surveyMeta, activeId]);

  useEffect(() => {
    setShowValidationErrors(false);
  }, [activeId, open]);

  useEffect(() => {
    if (!open) return;
    const el = surveyBodyRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeId, open]);

  const handleSubmit = async () => {
    if (!userId || !cohortCareOptionId) {
      window.alert('코호트 또는 사용자 정보를 찾을 수 없어 제출할 수 없습니다.');
      return;
    }
    if (!allInstrumentsComplete) {
      setShowValidationErrors(true);
      const firstIncomplete = activeSurveyCodes.find((code) => (remainingByCode[code] || 0) > 0);
      if (firstIncomplete) setActiveId(firstIncomplete);
      return;
    }
    setShowValidationErrors(false);

    setIsSubmitting(true);
    const codeToInstrumentId = new Map(
      surveyMeta.filter((s) => s.instrumentId).map((s) => [s.id, s.instrumentId])
    );
    const unresolvedCodes = activeSurveyCodes.filter((code) => !codeToInstrumentId.get(code));
    if (unresolvedCodes.length) {
      const { data: instrumentRows, error: instrumentErr } = await supabase
        .from('survey_instruments')
        .select('id, code')
        .in('code', unresolvedCodes);
      if (instrumentErr) {
        setIsSubmitting(false);
        console.error('[dashboardSurveyModal survey_instruments]', instrumentErr);
        window.alert('설문 도구 정보를 불러오지 못했습니다.');
        return;
      }
      (instrumentRows || []).forEach((row) => {
        if (row?.code && row?.id) codeToInstrumentId.set(row.code, row.id);
      });
    }

    const payload = activeSurveyCodes
      .map((code) => {
        const instrumentId = codeToInstrumentId.get(code);
        if (!instrumentId) return null;
        return {
          user_id: userId,
          cohort_care_option_id: cohortCareOptionId,
          phase,
          instrument_id: instrumentId,
          answers: answers[code] || {},
          submitted_at: new Date().toISOString(),
        };
      })
      .filter(Boolean);
    const { data: upsertedRows, error } = await supabase
      .from('survey_submissions')
      .upsert(payload, { onConflict: 'user_id,cohort_care_option_id,phase,instrument_id' })
      .select('id, instrument_id');

    if (error) {
      setIsSubmitting(false);
      console.error('[dashboardSurveyModal survey_submissions upsert]', error);
      window.alert('제출 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    const instrumentIdToCode = new Map();
    codeToInstrumentId.forEach((instrId, c) => {
      if (instrId) instrumentIdToCode.set(instrId, c);
    });
    const instrumentIds = activeSurveyCodes.map((c) => codeToInstrumentId.get(c)).filter(Boolean);
    let submissionRows = Array.isArray(upsertedRows) ? upsertedRows : [];
    if (submissionRows.length === 0 && instrumentIds.length) {
      const { data: refetched, error: refetchErr } = await supabase
        .from('survey_submissions')
        .select('id, instrument_id')
        .eq('user_id', userId)
        .eq('cohort_care_option_id', cohortCareOptionId)
        .eq('phase', phase)
        .in('instrument_id', instrumentIds);
      if (refetchErr) {
        console.error('[dashboardSurveyModal survey_submissions refetch]', refetchErr);
      } else {
        submissionRows = refetched || [];
      }
    }

    const resultPayloads = [];
    for (const row of submissionRows) {
      const code = instrumentIdToCode.get(row.instrument_id);
      if (!code) continue;
      const built = buildRowForInstrument(code, answers[code] || {});
      if (!built) continue;
      resultPayloads.push({
        submission_id: row.id,
        ...built,
        computed_at: new Date().toISOString(),
      });
    }

    let resultUpsertError = null;
    if (resultPayloads.length) {
      const { error: resErr } = await supabase
        .from('survey_submission_results')
        .upsert(resultPayloads, { onConflict: 'submission_id' });
      if (resErr) {
        console.error('[dashboardSurveyModal survey_submission_results upsert]', resErr);
        resultUpsertError = resErr;
      }
    }

    setIsSubmitting(false);

    setSubmitted((prev) => {
      const next = { ...prev };
      activeSurveyCodes.forEach((code) => {
        next[code] = true;
      });
      return next;
    });
    if (resultUpsertError) {
      window.alert(
        '응답은 저장되었으나 점수 집계(결과) 저장에 실패했습니다. 잠시 후 다시 제출하거나 관리자에게 문의해 주세요.'
      );
    } else {
      window.alert('제출되었습니다.');
    }
    onClose?.();
  };

  if (!open) return null;

  const meta =
    surveyMeta.find((s) => s.id === activeId) || SURVEY_META_BY_ID[activeId] || SURVEY_META[0];

  return (
    <div
      className="db-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="db-survey-modal-title"
      onClick={onClose}
    >
      <div className="db-modal db-modal--survey" onClick={(e) => e.stopPropagation()}>
        <div className="db-survey-modal-head">
          <h3 id="db-survey-modal-title" className="db-survey-modal-title">
            검사하기
          </h3>
          <button
            type="button"
            className="db-survey-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <p className="db-survey-modal-lead">질문지를 스크롤하여 선택하세요.</p>
        {isLoadingMeta && <p className="db-survey-modal-lead">설문 구성을 불러오는 중입니다...</p>}

        <div className="db-survey-tabs-strip">
          <div className="db-survey-tabs" role="tablist" aria-label="질문지 선택">
            {surveyMeta.map((s) => {
              const isOn = s.id === activeId;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  className={`db-survey-tab ${isOn ? 'db-survey-tab--active' : ''}`}
                  onClick={() => setActiveId(s.id)}
                >
                  <span className="db-survey-tab-title">
                    {s.shortTitle}
                    {(remainingByCode[s.id] ?? 1) === 0 && (questionsByCode[s.id] || []).length > 0 && (
                      <span className="db-survey-tab-check" aria-label="응답 완료">
                        v
                      </span>
                    )}
                  </span>
                  <span className="db-survey-tab-sub">
                    {s.title.replace(/\(.+\)\s*$/, '').trim()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="db-survey-body" ref={surveyBodyRef}>
          <h4 className="db-survey-sheet-title">{meta.title}</h4>
          {meta.info ? <p className="db-survey-block-intro">{meta.info}</p> : null}
          {isLoadingQuestions && (
            <p
              className="db-survey-block-intro db-survey-block-intro--loading"
              role="status"
              aria-live="polite"
            >
              <span className="db-spinner" aria-hidden />
            </p>
          )}
          {!isLoadingQuestions && !currentQuestions.length && (
            <p className="db-survey-block-intro">등록된 문항이 없습니다.</p>
          )}

          {!isLoadingQuestions && currentQuestions.length > 0 && (
            <div className="db-survey-fields">
              {currentQuestions.map((q, idx) => {
                const pairMeta = getNumberPairMeta(q);
                const value = pairMeta
                  ? {
                      hour: currentAnswerMap[pairMeta.hour.key] ?? '',
                      minute: currentAnswerMap[pairMeta.minute.key] ?? '',
                    }
                  : (currentAnswerMap[q.question_key] ?? '');
                const visible = isVisibleQuestion(q, currentAnswerMap);
                if (!visible) return null;
                const isInvalid = showValidationErrors && isQuestionEmpty(q, currentAnswerMap);
                const prev = idx > 0 ? currentQuestions[idx - 1] : null;
                const showSectionHeading = Boolean(
                  q.section_label &&
                  q.section_label !== prev?.section_label &&
                  getSectionHeading(activeId, q.section_label)
                );
                const sectionHeadingText = getSectionHeading(activeId, q.section_label);
                const withSectionHeading = (node) =>
                  showSectionHeading ? (
                    <>
                      <p className="db-survey-section-title">{sectionHeadingText}</p>
                      {node}
                    </>
                  ) : (
                    node
                  );

                if (q.input_type === 'time') {
                  return withSectionHeading(
                    <label key={q.question_key} className="db-survey-field">
                      <span className="db-label">{q.prompt}</span>
                      <input
                        type="time"
                        className={`db-input ${isInvalid ? 'db-input--error' : ''}`}
                        aria-invalid={isInvalid}
                        value={value}
                        onChange={(e) => setAnswer(activeId, q.question_key, e.target.value)}
                      />
                    </label>
                  );
                }

                if (q.input_type === 'number') {
                  const v = asObject(q.options);
                  if (pairMeta) {
                    const hourInvalid = showValidationErrors && isEmpty(value.hour);
                    const minuteInvalid = showValidationErrors && isEmpty(value.minute);
                    return withSectionHeading(
                      <div key={q.question_key} className="db-survey-field">
                        <span className="db-label">{q.prompt}</span>
                        <div className="db-survey-inline2">
                          <input
                            type="number"
                            min={Number.isFinite(pairMeta.hour.min) ? pairMeta.hour.min : 0}
                            max={Number.isFinite(pairMeta.hour.max) ? pairMeta.hour.max : 23}
                            className={`db-input ${hourInvalid ? 'db-input--error' : ''}`}
                            aria-invalid={hourInvalid}
                            placeholder={pairMeta.hour.label || '시간'}
                            value={value.hour}
                            onChange={(e) => setAnswer(activeId, pairMeta.hour.key, e.target.value)}
                          />
                          <input
                            type="number"
                            min={Number.isFinite(pairMeta.minute.min) ? pairMeta.minute.min : 0}
                            max={Number.isFinite(pairMeta.minute.max) ? pairMeta.minute.max : 59}
                            className={`db-input ${minuteInvalid ? 'db-input--error' : ''}`}
                            aria-invalid={minuteInvalid}
                            placeholder={pairMeta.minute.label || '분'}
                            value={value.minute}
                            onChange={(e) =>
                              setAnswer(activeId, pairMeta.minute.key, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    );
                  }
                  return withSectionHeading(
                    <label key={q.question_key} className="db-survey-field">
                      <span className="db-label">{q.prompt}</span>
                      <input
                        type="number"
                        min={Number.isFinite(v.min) ? v.min : undefined}
                        max={Number.isFinite(v.max) ? v.max : undefined}
                        className={`db-input ${isInvalid ? 'db-input--error' : ''}`}
                        aria-invalid={isInvalid}
                        value={value}
                        onChange={(e) => setAnswer(activeId, q.question_key, e.target.value)}
                      />
                    </label>
                  );
                }

                if (q.input_type === 'text') {
                  return withSectionHeading(
                    <label key={q.question_key} className="db-survey-field">
                      <span className="db-label">{q.prompt}</span>
                      <input
                        type="text"
                        className={`db-input ${isInvalid ? 'db-input--error' : ''}`}
                        aria-invalid={isInvalid}
                        value={value}
                        onChange={(e) => setAnswer(activeId, q.question_key, e.target.value)}
                      />
                    </label>
                  );
                }

                if (q.input_type === 'berlin_radio') {
                  const options = Array.isArray(q.options) ? q.options : [];
                  const disabled =
                    q.question_key === 'q9' && String(currentAnswerMap.q8 || '') !== 'y';
                  return withSectionHeading(
                    <div
                      key={q.question_key}
                      className={`db-survey-fieldset ${isInvalid ? 'db-survey-fieldset--error' : ''}`}
                    >
                      <p className="db-survey-question-label">{q.prompt}</p>
                      <BerlinRadio
                        name={`${activeId}-${q.question_key}`}
                        value={value}
                        options={options}
                        disabled={disabled}
                        onChange={(nextValue) => {
                          setAnswer(activeId, q.question_key, nextValue);
                          if (q.question_key === 'q8' && nextValue !== 'y') {
                            setAnswer(activeId, 'q9', '');
                          }
                        }}
                      />
                    </div>
                  );
                }

                if (
                  q.input_type === 'likert4' ||
                  q.input_type === 'likert5' ||
                  q.input_type === 'likert6'
                ) {
                  const labels = getLikertLabels(q);
                  const pointLabels = activeId === 'psqi' ? null : getLikertPointLabels(q, labels);

                  return withSectionHeading(
                    <div
                      key={q.question_key}
                      className={`db-survey-qblock ${isInvalid ? 'db-survey-qblock--error' : ''}`}
                    >
                      <span className="db-survey-qtext">{q.prompt}</span>
                      <Likert
                        name={`${activeId}-${q.question_key}`}
                        value={value}
                        labels={labels}
                        pointLabels={pointLabels}
                        onChange={(nextValue) => setAnswer(activeId, q.question_key, nextValue)}
                      />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          )}
        </div>

        <div className="db-survey-footer">
          <p className="db-survey-remaining" role="status">
            {allInstrumentsComplete ? (
              <>모든 질문지를 완료했습니다. 제출할 수 있습니다.</>
            ) : (
              <>
                아직 <strong>{totalRemaining}</strong>개 항목이 남았습니다.
              </>
            )}
          </p>
          <button
            type="button"
            className="db-btn db-btn--primary db-btn--block"
            disabled={isSubmitting || isLoadingQuestions || !allInstrumentsComplete}
            onClick={handleSubmit}
          >
            {isSubmitting ? '제출 중...' : '제출'}
          </button>
        </div>
      </div>
    </div>
  );
}
